import useCourseUnreadCount from "../CourseRealtime/useCourseUnreadCount";
import { courseApiEnabled } from "../../config/courseFeatures";
import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { FaChevronDown, FaChevronUp } from "react-icons/fa6";
import logo from "../../assets/Sidebar/Logo.png";
import AdminSideBarHrLine from "../../assets/Sidebar/adminSideBarHrLine.svg";
import { useLogOut } from "../../hooks/useLogOut";
import { useThunk } from "../../hooks/useThunk";
import { fetchUserUnreadThreadCount } from "../../store";
import useInProgressOrderCount from "./useInProgressOrderCount";

const UNREAD_THREAD_COUNT_REFRESH_EVENT = "unread-thread-count-refresh";

const Sidebar = ({ isSidebarOpen, setIsSidebarOpen, Menus }) => {
  const location = useLocation();
  const [openMenuIndices, setOpenMenuIndices] = useState([]);
  const [smallScreen, setIsSmallScreen] = useState(window.innerWidth < 1280);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const courseUnreadMessageCount = useCourseUnreadCount(courseApiEnabled && Menus.some(menu => menu.showCourseUnreadBadge), location.pathname);
  const inProgressOrderCount = useInProgressOrderCount(Menus.some(menu => menu.showInProgressBadge), location.pathname);

  const [logOut] = useLogOut();
  const [doFetchUserUnreadThreadCount] = useThunk(fetchUserUnreadThreadCount);

  const handleMenuClick = (index) => {
    const selectedMenu = Menus[index];
    if (selectedMenu.childs?.length > 0) {
      setOpenMenuIndices((prev) =>
        prev.includes(index)
          ? prev.filter((i) => i !== index)
          : [...prev, index]
      );
    }
  };

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 1280);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const activeParentIndex = Menus.findIndex((menu) =>
      menu.childs?.some((child) => child.link === location.pathname)
    );

    if (activeParentIndex >= 0) {
      setOpenMenuIndices((previous) => previous.includes(activeParentIndex)
        ? previous
        : [...previous, activeParentIndex]);
    }
  }, [Menus, location.pathname]);

  useEffect(() => {
    const fetchUnreadCount = async () => {
      const result = await doFetchUserUnreadThreadCount();
      if (result.success) {
        setUnreadMessageCount(result?.response?.data?.[0]?.count || 0);
      }
    };

    fetchUnreadCount();

    window.addEventListener(UNREAD_THREAD_COUNT_REFRESH_EVENT, fetchUnreadCount);

    return () => {
      window.removeEventListener(UNREAD_THREAD_COUNT_REFRESH_EVENT, fetchUnreadCount);
    };
  }, [doFetchUserUnreadThreadCount, location.pathname]);

  // log out method
  const logOutMethod = () => {
    logOut();
  };

  return (
    <div className="h-screen min-h-[460px] bg-[#0c297a] overflow-hidden relative pr-[3px]">
      <aside
        style={{ minHeight: "454px" }}
        className={`flex h-screen w-full flex-col bg-[#0c297a] text-white font-inter duration-300 transform ${
          smallScreen
            ? isSidebarOpen
              ? "translate-x-0"
              : "-translate-x-full"
            : "translate-x-0"
        }`}
      >
        {/* Logo Section */}
        <div className="text-white w-full flex flex-col pt-[23px] items-center justify-center">
          <div className="flex flex-row justify-center items-center ">
            <div className="text-white flex mt-[8px] justify-center sticky top-0 z-50">
              <img
                src={logo}
                alt="Main"
                className="ml-[12px] px-[8px] h-[40px] w-auto"
              />
            </div>
          </div>
          <img
            src={AdminSideBarHrLine}
            alt="Main"
            className="w-[224px] ml-4 mt-[24px]"
          />
        </div>

        {/* Main Menu */}
        <div
          className="w-full min-h-0 flex-1 overflow-y-auto overflow-x-hidden pb-6 scrollbar-webkit"
          style={{ paddingTop: "16px" }}
        >
          <ul className=" ">
            <span className="text-white text-xs ml-4">MY PORTAL</span>
            {Menus.map((menu, index) => {
              const hasChildren = menu.childs?.length > 0;
              const isChildActive = menu.childs?.some((child) => child.link === location.pathname);
              const menuItem = (
                <div
                    className={`px-[12px] py-[7px] m-auto  w-[208px] group flex items-center gap-x-2.5 mt-1.5 rounded-lg text-[15px] ${
                      menu.isLogout
                        ? "text-light-red hover:bg-white hover:text-light-red"
                        : (location.pathname === menu.link || isChildActive || (menu.link === "/courses" && location.pathname.startsWith("/courses/")))
                        ? "bg-white text-blue-shades"
                        : "text-white"
                    } ${menu.isLogout ? "" : "hover:bg-white hover:text-blue-shades"}`}
                  >
                    <span className="flex h-[18px] w-[18px] items-center justify-center [&>svg]:h-[18px] [&>svg]:w-[18px]">{menu.icon}</span>
                    <span className="font-medium w-full">{menu.title}</span>
                    {menu.showUnreadBadge && (
                      <span className="ml-auto inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-light-red px-2 text-[11px] font-bold leading-none text-white">
                        {unreadMessageCount}
                      </span>
                    )}
                    {menu.showInProgressBadge && (
                      <span
                        title={`${inProgressOrderCount} in-progress orders`}
                        aria-label={`${inProgressOrderCount} in-progress orders`}
                        className="ml-auto inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-light-red px-2 text-[11px] font-bold leading-none text-white"
                      >
                        {inProgressOrderCount}
                      </span>
                    )}
                    {menu.showCourseUnreadBadge && courseUnreadMessageCount > 0 && (
                      <span title={`${courseUnreadMessageCount} unread course messages`} aria-label={`${courseUnreadMessageCount} unread course messages`} className="ml-auto inline-flex h-6 min-w-6 shrink-0 items-center justify-center rounded-full bg-light-red px-2 text-[11px] font-bold leading-none text-white">
                        {courseUnreadMessageCount > 99 ? "99+" : courseUnreadMessageCount}
                      </span>
                    )}
                    {hasChildren && (
                      <span className="">
                        {openMenuIndices.includes(index) ? (
                          <FaChevronUp />
                        ) : (
                          <FaChevronDown />
                        )}
                      </span>
                    )}
                </div>
              );

              return (
              <div key={index} className="relative w-fuLl ml-4 min-h-[34px] ">
                {hasChildren ? (
                  <button
                    type="button"
                    className="w-full text-left"
                    aria-expanded={openMenuIndices.includes(index)}
                    onClick={() => handleMenuClick(index)}
                  >
                    {menuItem}
                  </button>
                ) : (
                  <Link
                    to={menu.link || "#"}
                    className="w-full"
                    onClick={(event) => {
                      if (menu.isLogout) {
                        event.preventDefault();
                        logOutMethod();
                        return;
                      }
                      setIsSidebarOpen(false);
                    }}
                  >
                    {menuItem}
                  </Link>
                )}
                {hasChildren && openMenuIndices.includes(index) && (
                  <ul className="w-[208px] space-y-0.5">
                    {menu.childs.map((child, childIndex) => {
                      const childClassName = `block px-[40px] font-medium py-[7px] mx-2 w-[208px] mt-1.5 rounded-lg text-[15px] ${
                        child.isLogout
                          ? "text-light-red hover:bg-white hover:text-light-red"
                          : location.pathname === child.link
                            ? "bg-white text-blue-shades"
                            : "hover:bg-white hover:text-blue-shades text-white"
                      }`;

                      return (
                        <li key={childIndex}>
                          {child.isLogout ? (
                            <button
                              type="button"
                              className={`${childClassName} text-left`}
                              onClick={logOutMethod}
                            >
                              {child.title}
                            </button>
                          ) : (
                            <Link
                              to={child.link || "#"}
                              className={childClassName}
                              onClick={() => setIsSidebarOpen(false)}
                            >
                              {child.title}
                            </Link>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
                {Menus[index + 1]?.link === "/terms-and-conditions" && (
                  <>
                    <div className="w-[220px] h-[2px] my-2.5 bg-white rounded-full"></div>
                    <span className="text-xs text-white ">TERMS AND POLICY</span>
                  </>
                )}
                 {Menus[index + 1]?.link === "/in-progress" && (
                  <>
                    <div className="w-[220px] h-[2px] my-2.5 bg-white rounded-full"></div>
                    <span className="text-xs text-white ">ORDERS</span>
                  </>
                )}
              </div>
              );
            })}
          </ul>
        </div>
      </aside>

      {/* Close Button for mobile screens */}
      {smallScreen && (
        <button
          onClick={() => setIsSidebarOpen(false)}
          className="absolute top-4 right-4 xl:hidden text-white text-2xl"
        >
          &times;
        </button>
      )}

      {/* Overlay */}
      {smallScreen && isSidebarOpen && (
        <div
          onClick={() => setIsSidebarOpen(false)}
          className="fixed inset-0 bg-black opacity-50"
          style={{
            clipPath: `inset(0 0 0 ${isSidebarOpen ? "256px" : "0px"})`,
          }}
        ></div>
      )}
    </div>
  );
};

export default Sidebar;
